import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { PlacementForm } from "@/components/placements/placement-form";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import type { Placement } from "@/types";

export function PlacementEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const placementId = Number(id);

  const [placement, setPlacement] = useState<Placement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchPlacement() {
      try {
        const data = await adminApi.placements.get(placementId);
        setPlacement(data);
      } catch (err) {
        toast.error("Failed to load placement", {
          description: describeApiError(err),
        });
        navigate("/placements");
      } finally {
        setIsLoading(false);
      }
    }
    fetchPlacement();
  }, [placementId, navigate]);

  async function handleSubmit(data: Partial<Placement>) {
    setIsSaving(true);
    try {
      await adminApi.placements.update(placementId, data);
      toast.success("Placement updated successfully");
      navigate("/placements");
    } catch (err) {
      toast.error("Failed to update placement", {
        description: describeApiError(err),
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center px-6 py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!placement) return null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={`Edit: ${placement.label}`}
        description="Update placement details"
        actions={
          <Button variant="outline" onClick={() => navigate("/placements")}>
            <ArrowLeft className="mr-2 size-4" />
            Back to Placements
          </Button>
        }
      />
      <PlacementForm initialData={placement} onSubmit={handleSubmit} isLoading={isSaving} />
    </div>
  );
}
